import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { ReactComponent as DownIcon } from '../icons/ic-down.svg';

const SelectBox = forwardRef(
  (
    {
      disabled,
      width,
      options,
      onSelect,
      defaultOption = null,
      header = false,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(defaultOption);
    const [optionListHeight, setOptionListHeight] = useState(0);
    const optionListRef = useRef(null);
    const selectBoxRef = useRef(null);

    const handleOptionClick = (option) => {
      setSelectedOption(option);
      setIsOpen(false);
      onSelect(option);
    };

    const handleOpen = () => {
      setIsOpen(!isOpen);
    };

    useImperativeHandle(ref, () => ({
      handleOpen,
    }));

    useEffect(() => {
      if (optionListRef.current) {
        const height = optionListRef.current.scrollHeight;
        setOptionListHeight(height);
      }

      const clickOutside = (e) => {
        if (isOpen && !selectBoxRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', clickOutside);

      return () => {
        document.removeEventListener('mousedown', clickOutside);
      };
    }, [isOpen]);

    return (
      <Container
        ref={selectBoxRef}
        width={width}
        isOpen={isOpen}
        selected={selectedOption}
        isHeader={header}
        disabled={disabled}
        onClick={
          !disabled
            ? () => {
                if (!selectedOption || header) setIsOpen(!isOpen);
              }
            : null
        }
      >
        {(!selectedOption || header) && <Icon $isOpen={isOpen} />}

        <Label selected={selectedOption} disabled={disabled}>
          {selectedOption || '쇼핑몰 선택'}
        </Label>
        <OptionList
          isOpen={isOpen}
          ref={optionListRef}
          height={optionListHeight}
        >
          {options.map((option) => (
            <Option
              key={option}
              selected={selectedOption === option}
              onClick={() => handleOptionClick(option)}
            >
              {option}
            </Option>
          ))}
        </OptionList>
      </Container>
    );
  }
);

export default SelectBox;

const Container = styled.div`
  position: relative;
  height: 38px;
  border: 1px solid;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  width: ${({ width }) => width}px;
  border-radius: ${({ isOpen }) => (isOpen ? '5px 5px 0 0' : '5px')};
  border-color: #cccccc;
  transition: border-radius 0.3s ease;

  ${(props) =>
    props.selected &&
    !props.isOpen &&
    !props.isHeader &&
    `
      border-color: #ff7a00;
      background-color: #fff0db;

      color: #ff7a00;
      &::before {
        content: '선택';
        position: absolute;
        right: 20px;
        top: 50%;
        transform: translateY(-50%);
        padding: 2px 7px;
        color: #ffffff; 
        font-size: 14px;
        background-color: #ffb600;
        border-radius: 3px;
      }`}
`;

const Icon = styled(DownIcon)`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%)
    rotate(${({ $isOpen }) => ($isOpen ? '-180deg' : '0')});
  transition: transform 0.3s ease;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: 13px;
  font-size: 18px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ selected }) => (selected ? '#FF7A00' : '#b5b5b5')};
`;

const OptionList = styled.ul`
  position: absolute;
  left: -1px;
  width: 100%;
  list-style: none;
  border-radius: 0 0 5px 5px;
  padding: 0;
  overflow: hidden;
  background-color: #fff;
  z-index: 1;
  transition: max-height 0.3s ease, opacity 0.3s ease;

  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  border: ${({ isOpen }) => (isOpen ? '1px solid #cccccc' : 'none')};
  max-height: ${({ isOpen, height }) => (isOpen ? `${height}px` : '0')};
`;

const Option = styled.li`
  padding: 10px 0 0 13px;
  font-size: 18px;
  &:last-child {
    padding-bottom: 15px;
  }

  ${(props) =>
    props.selected &&
    `
      color: #ff7a00;
      &::after {
        content: '선택';
        position: absolute;
        right: 20px;
        padding: 3px 7px;
        color: #ffffff;
        font-size: 14px;
        background-color: #ffb600;
        border-radius: 3px;
      }`}
`;
